import { Divider, Stack } from '@/components/layout';
import { Button, Text, TextInput } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useChangePassword } from '@/features/auth/hooks/use-change-password';
import MutatePlayerView from '@/features/player/components/mutate-player';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import React from 'react';
import { StyleSheet } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export function ChangePasswordScreen() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();

  const {
    currentPassword,
    newPassword,
    confirmPassword,
    confirmMismatchError,
    fieldErrors,
    onChangeCurrentPassword,
    onChangeNewPassword,
    onChangeConfirmPassword,
    handleCancel,
    handleUpdatePassword,
  } = useChangePassword();

  const sectionHeader = (
    <Stack gap={scale({ phone: 20, tablet: 16 })}>
      <Text
        fontWeight="700"
        fontSize={scale({ phone: 11, tablet: 14 })}
        fontFamily={isTablet ? 'Inter' : 'Montserrat'}
        style={styles.sectionTitle}
        transform="uppercase">
        {STRINGS.auth.changePassword.sectionTitle}
      </Text>
      <Text variant={'Font Inter/Subtitle/Regular 14px'} style={styles.sectionDescription}>
        {STRINGS.auth.changePassword.sectionDescription}
      </Text>
    </Stack>
  );

  const passwordFields = (
    <>
      <TextInput
        value={currentPassword}
        onChangeText={onChangeCurrentPassword}
        status={fieldErrors.currentPassword ? 'error' : 'default'}
        defaultSecureTextEntry>
        <TextInput.Content>
          <TextInput.Icon size={scale({ phone: 16, tablet: 18 })} name="IcLock" position="left" />
          <TextInput.Field
            placeholder={STRINGS.auth.changePassword.placeholderCurrent}
            autoCapitalize="none"
            maxLength={50}
            autoCorrect={false}
            placeholderTextColor={colors['4']}
          />
          <TextInput.PasswordToggle size={scale({ phone: 16, tablet: 20 })} />
        </TextInput.Content>
        {fieldErrors.currentPassword ? (
          <TextInput.HelperText
            variant="Font Montserrat/Subtitle/Minimize 1 Regular 11px"
            fontStyle="italic"
            style={styles.helperText}>
            {fieldErrors.currentPassword}
          </TextInput.HelperText>
        ) : null}
      </TextInput>

      <TextInput
        value={newPassword}
        onChangeText={onChangeNewPassword}
        status={fieldErrors.newPassword ? 'error' : 'default'}
        defaultSecureTextEntry>
        <TextInput.Content>
          <TextInput.Icon size={scale({ phone: 16, tablet: 18 })} name="IcLock" position="left" />
          <TextInput.Field
            placeholder={STRINGS.auth.changePassword.placeholderNew}
            autoCapitalize="none"
            maxLength={50}
            autoCorrect={false}
            placeholderTextColor={colors['4']}
          />
          <TextInput.PasswordToggle size={scale({ phone: 16, tablet: 20 })} />
        </TextInput.Content>
        {fieldErrors.newPassword ? (
          <TextInput.HelperText
            variant="Font Montserrat/Subtitle/Minimize 1 Regular 11px"
            fontStyle="italic"
            style={styles.helperText}>
            {fieldErrors.newPassword}
          </TextInput.HelperText>
        ) : null}
      </TextInput>

      <TextInput
        value={confirmPassword}
        onChangeText={onChangeConfirmPassword}
        status={confirmMismatchError || fieldErrors.confirmPassword ? 'error' : 'default'}
        defaultSecureTextEntry>
        <TextInput.Content>
          <TextInput.Icon size={scale({ phone: 16, tablet: 18 })} name="IcLock" position="left" />
          <TextInput.Field
            placeholder={STRINGS.auth.changePassword.placeholderConfirm}
            autoCapitalize="none"
            maxLength={50}
            autoCorrect={false}
            placeholderTextColor={colors['4']}
          />
          <TextInput.PasswordToggle size={scale({ phone: 16, tablet: 20 })} />
        </TextInput.Content>
        {confirmMismatchError || fieldErrors.confirmPassword ? (
          <TextInput.HelperText
            variant="Font Montserrat/Subtitle/Minimize 1 Regular 11px"
            fontStyle="italic"
            style={styles.helperText}>
            {confirmMismatchError ?? fieldErrors.confirmPassword}
          </TextInput.HelperText>
        ) : null}
      </TextInput>
    </>
  );

  const footerButtons = (
    <Stack gap={scale(16)} width="100%">
      <Button onPress={handleUpdatePassword} style={styles.primaryButton}>
        <Button.Text variant="Font Inter/Subtitle/Regular 11px" transform="uppercase">
          {STRINGS.auth.changePassword.buttonUpdate}
        </Button.Text>
      </Button>

      <Button onPress={handleCancel} variant="outline" style={styles.secondaryButton}>
        <Button.Text style={styles.cancelText} variant="Font Inter/Subtitle/Regular 11px">
          {STRINGS.auth.changePassword.buttonCancel}
        </Button.Text>
      </Button>
    </Stack>
  );

  const tabletCardButtons = (
    <Stack gap={scale(16)} width="100%">
      <Button
        onPress={handleUpdatePassword}
        style={[styles.primaryButton, styles.tabletCardButton]}>
        <Button.Text variant="Font Inter/Subtitle/Medium 12px" transform="uppercase">
          {STRINGS.auth.changePassword.buttonUpdate}
        </Button.Text>
      </Button>

      <Button
        onPress={handleCancel}
        variant="outline"
        style={[styles.secondaryButton, styles.tabletCardButton]}>
        <Button.Text style={styles.cancelText} variant="Font Inter/Subtitle/Medium 12px">
          {STRINGS.auth.changePassword.buttonCancel}
        </Button.Text>
      </Button>
    </Stack>
  );

  return (
    <Stack flex={1} backgroundColor={isTablet ? colors['1'] : colors['0']}>
      <MutatePlayerView.Header title={STRINGS.auth.changePassword.title} isBoldTitle />
      <Divider color={colors['5']} spacing={scale(0)} thickness={StyleSheet.hairlineWidth} />

      <KeyboardAwareScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.formContent,
          isTablet && styles.formContentTablet,
          isTablet && { paddingBottom: insets.bottom + scale(24) },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={scale(12)}
        extraKeyboardSpace={scale(12)}>
        {isTablet ? (
          <Stack style={styles.tabletCard} gap={scale(16)} width="100%" maxWidth={scale(375)}>
            {sectionHeader}
            {passwordFields}
            {tabletCardButtons}
          </Stack>
        ) : (
          <Stack width="100%" maxWidth={scale(676)} gap={scale(24)} alignSelf="center">
            {sectionHeader}
            {passwordFields}
          </Stack>
        )}
      </KeyboardAwareScrollView>

      {!isTablet ? (
        <Stack
          backgroundColor={colors['0']}
          paddingTop={scale(16)}
          paddingBottom={insets.bottom + scale(12)}
          paddingX={scale({ phone: 40, tablet: 0 })}
          style={styles.footer}>
          {footerButtons}
        </Stack>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  scroll: {
    flex: 1,
    backgroundColor: isTablet ? colors['1'] : colors['0'],
  },
  formContent: {
    paddingHorizontal: scale(18),
    paddingTop: scale(16),
    paddingBottom: scale(20),
    ...(isTablet ? { flexGrow: 1 } : {}),
  },
  formContentTablet: {
    paddingHorizontal: scale(24),
    paddingTop: scale(24),
    alignItems: 'center',
  },
  tabletCard: {
    backgroundColor: colors['0'],
    padding: scale(24),
  },
  sectionTitle: {
    color: colors.Blue,
  },
  sectionDescription: {
    color: colors['6'],
  },
  footer: {},
  cancelText: {
    textTransform: 'uppercase',
  },
  primaryButton: {
    minHeight: scale(30),
    height: scale(30),
    paddingVertical: scale(0),
  },
  secondaryButton: {
    minHeight: scale(30),
    height: scale(30),
    paddingVertical: scale(0),
  },
  tabletCardButton: {
    alignSelf: 'stretch',
    minHeight: scale(40),
    height: scale(40),
  },
  helperText: {
    color: colors.Red,
  },
}));
